<template>
  <div class="space-y-4">
    <h2>{{ t('crossborderCountries') }}</h2>
    <div class="space-y-4 rounded-md bg-white p-4">
      <div v-if="countryCodes.length" class="flex flex-wrap gap-2">
        <div
          v-for="countryCode in countryCodes"
          :key="countryCode"
          class="inline-flex items-center gap-1 rounded-full bg-primary px-2.5 py-1 text-xs font-medium text-white"
        >
          <span>{{ getCountryLabel(countryCode) }}</span>
          <button
            class="text-white/80 hover:text-white disabled:opacity-40"
            :disabled="countryCodes.length <= 1"
            @click.prevent="removeCountryFromPool(countryCode)"
          >
            <XIcon class="size-3" />
          </button>
        </div>
      </div>
      <div
        v-else
        class="inline-flex items-center rounded-full bg-obsidian-100 px-2.5 py-1 text-xs font-medium text-obsidian-700"
      >
        {{ t('noCountries') }}
      </div>

      <div class="flex items-end gap-2">
        <template v-if="showCountrySelect">
          <SearchableSelect
            v-model="selectedCountry"
            class="flex-1"
            :label="t('countries')"
            :options="availableCountryOptions"
            :placeholder="t('chooseCountry')"
            :track-by="['label', 'labelEn', 'labelShort', 'value', 'alternativeNames']"
          />
          <CVButton
            variant="secondary"
            size="sm"
            :disabled="!selectedCountry"
            @click.prevent="addCountryToPool"
          >
            {{ t('add') }}
          </CVButton>
          <CVButton variant="ghost" size="sm" @click.prevent="hideCountrySelect">
            {{ t('cancel') }}
          </CVButton>
        </template>
        <CVButton v-else variant="ghost" size="sm" @click.prevent="showCountrySelect = true">
          + {{ t('addCountry') }}
        </CVButton>
      </div>

      <InputHint :hint="countryCodesError" :is-error="!!countryCodesError" />

      <div class="space-y-3 border-t pt-4">
        <div
          v-for="(field, index) in customLabelFields"
          :key="field.key"
          class="relative border-b pb-3 pt-1 last:border-b-0 last:pb-0"
        >
          <div class="flex items-end gap-4">
            <div class="grid flex-1 grid-cols-4 gap-3">
              <SelectField
                :name="`crossborderCountryCustomLabels[${index}].code`"
                :label="t('countries')"
                :options="getCustomLabelCountryOptions(index)"
                :placeholder="t('chooseCountry')"
              />
              <InputField
                :name="`crossborderCountryCustomLabels[${index}].labels.de`"
                :label="t('labelDe')"
                type="text"
              />
              <InputField
                :name="`crossborderCountryCustomLabels[${index}].labels.en`"
                :label="t('labelEn')"
                type="text"
              />
              <InputField
                :name="`crossborderCountryCustomLabels[${index}].labels.es`"
                :label="t('labelEs')"
                type="text"
              />
            </div>
            <button
              class="px-2 py-3 text-bittersweet-600"
              @click.prevent="removeCustomLabel(index)"
            >
              <XIcon class="size-4" />
            </button>
          </div>
        </div>

        <div v-if="canAddCustomLabel" class="flex justify-center">
          <CVButton variant="ghost" @click.prevent="addCustomLabel">
            {{ t('addCustomLabel') }}
          </CVButton>
        </div>

        <InputHint :hint="customLabelsError" :is-error="!!customLabelsError" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import InputField from '@/components/InputField.vue';
import InputHint from '@/components/InputHint.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import SearchableSelect from '@/components/SearchableSelect.vue';
import SelectField from '@/components/SelectField.vue';
import { getCountries } from '@/utils/countries';
import { useFieldArray, useFieldError, useFormContext } from 'vee-validate';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { XIcon } from 'lucide-vue-next';
import { Alert } from '@/services/alert.service';
import type { CrossborderCountryCustomLabelFormEntry } from './crossborder-countries-form.utils';

const { t, locale } = useI18n();
const { values, setFieldValue } = useFormContext<{
  crossborderCountryCodes?: string[];
  crossborderCountryCustomLabels?: CrossborderCountryCustomLabelFormEntry[];
}>();
const countryCodesError = useFieldError('crossborderCountryCodes');
const customLabelsError = useFieldError('crossborderCountryCustomLabels');

const {
  fields: customLabelFields,
  push,
  remove,
} = useFieldArray<CrossborderCountryCustomLabelFormEntry>('crossborderCountryCustomLabels');

const showCountrySelect = ref(false);
const selectedCountry = ref<string | undefined>();

const allCountries = computed(() => getCountries(locale.value));
const countryCodes = computed(() => values.crossborderCountryCodes ?? []);

const availableCountryOptions = computed(() => {
  const selected = new Set(countryCodes.value);
  return allCountries.value.filter((country) => !selected.has(country.value));
});

const getCountryLabel = (countryCode: string) =>
  allCountries.value.find((country) => country.value === countryCode)?.label ?? countryCode;

const getCustomLabelCountryOptions = (index: number) => {
  const currentCode = customLabelFields.value[index]?.value.code;
  const usedByOtherRows = new Set(
    customLabelFields.value
      .filter((_, rowIndex) => rowIndex !== index)
      .map((field) => field.value.code)
      .filter(Boolean),
  );

  return countryCodes.value
    .filter((code) => code === currentCode || !usedByOtherRows.has(code))
    .map((code) => ({
      label: getCountryLabel(code),
      value: code,
    }));
};

const hasIncompleteCustomLabelRow = computed(() =>
  customLabelFields.value.some((field) => !field.value.code),
);

const canAddCustomLabel = computed(() => {
  if (!countryCodes.value.length || hasIncompleteCustomLabelRow.value) {
    return false;
  }
  const assignedCodes = new Set(
    customLabelFields.value.map((field) => field.value.code).filter(Boolean),
  );
  return countryCodes.value.some((code) => !assignedCodes.has(code));
});

const hideCountrySelect = () => {
  showCountrySelect.value = false;
  selectedCountry.value = undefined;
};

const addCountryToPool = () => {
  if (!selectedCountry.value || countryCodes.value.includes(selectedCountry.value)) {
    return;
  }
  setFieldValue('crossborderCountryCodes', [...countryCodes.value, selectedCountry.value] as never);
  hideCountrySelect();
};

const removeCountryFromPool = async (countryCode: string) => {
  if (countryCodes.value.length <= 1) {
    return;
  }

  const { isDenied } = await Alert.fire({
    text: t('reallyDeleteCountry'),
    icon: 'warning',
    showConfirmButton: false,
    showDenyButton: true,
    showCancelButton: true,
    denyButtonText: t('delete'),
    cancelButtonText: t('cancel'),
  });
  if (!isDenied) {
    return;
  }

  setFieldValue(
    'crossborderCountryCodes',
    countryCodes.value.filter((code) => code !== countryCode) as never,
  );
  setFieldValue(
    'crossborderCountryCustomLabels',
    (values.crossborderCountryCustomLabels ?? []).filter(
      (entry) => entry.code !== countryCode,
    ) as never,
  );
};

const addCustomLabel = () => {
  if (!canAddCustomLabel.value) {
    return;
  }
  push({ code: '', labels: { de: '', en: '', es: '' } });
};

const removeCustomLabel = async (index: number) => {
  const { isDenied } = await Alert.fire({
    text: t('reallyDeleteCustomLabel'),
    icon: 'warning',
    showConfirmButton: false,
    showDenyButton: true,
    showCancelButton: true,
    denyButtonText: t('delete'),
    cancelButtonText: t('cancel'),
  });
  if (!isDenied) {
    return;
  }
  remove(index);
};
</script>

<i18n lang="json">
{
  "en": {
    "crossborderCountries": "Crossborder countries",
    "addCountry": "Add country",
    "chooseCountry": "Choose country",
    "countries": "Country",
    "add": "Add",
    "cancel": "Cancel",
    "delete": "Delete",
    "noCountries": "No countries selected",
    "reallyDeleteCountry": "Do you really want to remove this country?",
    "reallyDeleteCustomLabel": "Do you really want to delete this custom name?",
    "addCustomLabel": "+ Add custom name",
    "labelDe": "Label DE",
    "labelEn": "Label EN",
    "labelEs": "Label ES"
  },
  "de": {
    "crossborderCountries": "Crossborder-Länder",
    "addCountry": "Land hinzufügen",
    "chooseCountry": "Land auswählen",
    "countries": "Land",
    "add": "Hinzufügen",
    "cancel": "Abbrechen",
    "delete": "Löschen",
    "noCountries": "Keine Länder ausgewählt",
    "reallyDeleteCountry": "Möchtest du dieses Land wirklich entfernen?",
    "reallyDeleteCustomLabel": "Möchtest du diese individuelle Benennung wirklich löschen?",
    "addCustomLabel": "+ Individuelle Benennung hinzufügen",
    "labelDe": "Bezeichnung DE",
    "labelEn": "Bezeichnung EN",
    "labelEs": "Bezeichnung ES"
  },
  "es": {
    "crossborderCountries": "Países crossborder",
    "addCountry": "Añadir país",
    "chooseCountry": "Elegir país",
    "countries": "País",
    "add": "Añadir",
    "cancel": "Cancelar",
    "delete": "Eliminar",
    "noCountries": "Ningún país seleccionado",
    "reallyDeleteCountry": "¿Seguro que quieres eliminar este país?",
    "reallyDeleteCustomLabel": "¿Seguro que quieres eliminar este nombre personalizado?",
    "addCustomLabel": "+ Añadir nombre personalizado",
    "labelDe": "Etiqueta DE",
    "labelEn": "Etiqueta EN",
    "labelEs": "Etiqueta ES"
  }
}
</i18n>
